/**
 * DropdownAtom.tsx
 *
 * A generic dropdown atom wrapping PrimeReact's Dropdown component
 * with design system styling. Use this for simple dropdowns.
 * For sphere selection with avatars, use PrimeReactSingleSelect instead.
 */

import React, { forwardRef } from 'react';
import { useTranslation } from 'localization';
import { Dropdown, DropdownProps } from 'primereact/dropdown';
import type {
  DropdownChangeEvent as PrimeDropdownChangeEvent,
  DropdownPassThroughMethodOptions,
  DropdownPassThroughOptions,
  DropdownPassThroughType,
} from 'primereact/dropdown';
import { dropdownAccessibility } from './dropdownAccessibility';
import './DropdownAtom.scss';

export type DropdownVariant = 'default' | 'outlined' | 'filled';
export type DropdownSize = 'compact' | 'small' | 'normal' | 'large';

/** Re-export the DropdownChangeEvent type from PrimeReact */
export type DropdownChangeEvent = PrimeDropdownChangeEvent;

export interface DropdownAtomProps extends Omit<DropdownProps, 'className' | 'size' | 'variant' | 'panelClassName'> {
  /** Visual variant of the dropdown */
  variant?: DropdownVariant;
  /** Size of the dropdown */
  dropdownSize?: DropdownSize;
  /** Additional CSS class names */
  className?: string;
  /** Additional CSS class names for the dropdown panel */
  panelClassName?: string;
  /**
   * Attributes for the dropdown's real keyboard control — the visually hidden focus
   * `<input>`. PrimeReact's `pt.input` cannot target it: the same key also styles the
   * visible label `<span>`, so anything set there lands on both.
   */
  focusInputAttrs?: Record<string, string | undefined>;
}

/** The marker a hosting surface reads to tell "Escape closed this list" from "Escape closed me". */
const POPUP_OPEN_MARKER = { 'data-popup-open': 'true' } as const;

/**
 * Layer the open-popup marker over one pass-through section, whichever form (object or
 * `(options) => object`) the consumer used, so a caller's own `pt` is never silently dropped.
 * Read from PrimeReact's own `state.overlayVisible` rather than from `onShow`/`onHide`:
 * those fire after the panel's enter/leave transition, and an Escape raised in that window
 * would read as the surface's.
 */
function withPopupMarker<T extends object>(
  consumer: DropdownPassThroughType<T> | undefined
): DropdownPassThroughType<T> {
  return (options?: DropdownPassThroughMethodOptions) => {
    const base = typeof consumer === 'function' ? consumer(options) : consumer;
    return { ...(base ?? {}), ...(options?.state.overlayVisible ? POPUP_OPEN_MARKER : {}) } as T;
  };
}

/**
 * Give the filter box a name the consumer did not supply. PrimeReact renders the `filter`
 * input with no label and no `aria-label` of its own, so without this every filterable
 * dropdown in the library ships an unnamed text input. A consumer's `pt.filterInput`
 * (either form) wins when it carries a name.
 */
function withDefaultName<T extends { 'aria-label'?: string; 'aria-labelledby'?: string }>(
  consumer: DropdownPassThroughType<T> | undefined,
  name: string
): DropdownPassThroughType<T> {
  return (options?: DropdownPassThroughMethodOptions) => {
    const base = typeof consumer === 'function' ? consumer(options) : consumer;
    const named = base?.['aria-label'] || base?.['aria-labelledby'];
    return { ...(named ? {} : { 'aria-label': name }), ...(base ?? {}) } as T;
  };
}

const DropdownAtom = forwardRef<Dropdown, DropdownAtomProps>((props, ref) => {
  const { t } = useTranslation();
  const {
    variant = 'default',
    dropdownSize = 'normal',
    className = '',
    panelClassName = '',
    onShow,
    focusInputAttrs,
    ...rest
  } = props;

  // PrimeReact's Dropdown (10.9) sets `aria-expanded` on its trigger but gives the portaled
  // panel no id and the trigger no `aria-controls`. Both are stamped here on show — the
  // WAI-ARIA disclosure link — so assistive tech and the parity gate (GEO-2) can tell a
  // listbox anchored inside a popover from an unrelated overlay lying over it. The panel is
  // found by a per-instance class: it lives under `document.body`, not under this element.
  const panelToken = `dropdown-atom-panel--${React.useId().replaceAll(':', '')}`;
  const panelId = `${panelToken}-panel`;
  const innerRef = React.useRef<Dropdown | null>(null);
  const setRefs = (el: Dropdown | null) => {
    innerRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as React.MutableRefObject<Dropdown | null>).current = el;
  };
  const linkPanel = () => {
    const panel = document.querySelector<HTMLElement>(`.${panelToken}`);
    const root = innerRef.current?.getElement?.() ?? null;
    if (panel && !panel.id) panel.id = panelId;
    const trigger = root?.querySelector<HTMLElement>('input, [role="combobox"]') ?? root;
    if (panel && trigger) trigger.setAttribute('aria-controls', panel.id);
  };

  const { nameAttrs, ariaLabel, ariaLabelledBy, pt: accessiblePt } = dropdownAccessibility(rest);

  // A surface that hosts this control (a dialog, a non-modal rail) has to tell an Escape
  // that closes THIS list from one that should close the surface around it — and PrimeReact
  // calls `preventDefault()` on both alike, so the key event cannot say. While the list is
  // open, the root and the option panel carry `data-popup-open`; `useDialogFocus` reads that
  // off the event target's ancestors. Both are marked because the panel is portalled out of
  // the root unless `appendTo="self"`, and its filter input raises its own Escapes.
  const pt: DropdownPassThroughOptions = {
    ...accessiblePt,
    root: withPopupMarker(accessiblePt?.root),
    panel: withPopupMarker(accessiblePt?.panel),
    ...(rest.filter
      ? { filterInput: withDefaultName(accessiblePt?.filterInput, t('common.actions.filterOptions')) }
      : {}),
  };

  const focusInputKey = focusInputAttrs ? JSON.stringify(focusInputAttrs) : '';
  React.useLayoutEffect(() => {
    if (!focusInputAttrs) return;
    const input = innerRef.current?.getFocusInput();
    if (!input) return;
    for (const [name, value] of Object.entries(focusInputAttrs)) {
      if (value === undefined) input.removeAttribute(name);
      else input.setAttribute(name, value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the attrs' content, not identity
  }, [focusInputKey]);

  // Build CSS classes
  const classes = [
    'dropdown-atom',
    `dropdown-atom--variant-${variant}`,
    `dropdown-atom--size-${dropdownSize}`,
    className,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  // Build panel CSS classes
  const panelClasses = [
    'dropdown-atom-panel',
    panelToken,
    panelClassName,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  return (
    <Dropdown
      ref={setRefs}
      className={classes}
      panelClassName={panelClasses}
      onShow={() => {
        linkPanel();
        onShow?.();
      }}
      {...rest}
      {...nameAttrs}
      ariaLabel={ariaLabel}
      ariaLabelledBy={ariaLabelledBy}
      pt={pt}
    />
  );
});

DropdownAtom.displayName = 'DropdownAtom';

export default DropdownAtom;
